/**
 * SharedListDetailPage
 *
 * Page at /app/shared-lists/:listId. Shows list tasks, members, and management
 * actions based on the current user's role.
 *
 * Role capabilities:
 *   owner   — rename, add/delete tasks, invite, change roles, remove members, reset, delete list
 *   editor  — rename, add/delete tasks, invite, reset
 *   checker — toggle task completion only
 *   viewer  — read only
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Loader2,
  Plus,
  Trash2,
  ArrowLeft,
  RotateCcw,
  LogOut,
  UserPlus,
  ChevronDown,
  Check,
} from 'lucide-react';
import { Layout } from '../../components/Layout';
import {
  getSharedList,
  renameSharedList,
  deleteSharedList,
  createSharedListTask,
  updateSharedListTask,
  deleteSharedListTask,
  resetSharedListTasks,
  inviteToSharedList,
  changeSharedListMemberRole,
  removeSharedListMember,
  leaveSharedList,
} from '../../api/shared-lists';
import type { SharedListDetail, SharedListTask, SharedListMemberDisplay } from '../../api/shared-lists';
type SharedListMember = SharedListMemberDisplay;

const ROLES = ['editor', 'checker', 'viewer'] as const;
type Role = (typeof ROLES)[number];

function roleLabel(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function canWrite(role: string): boolean {
  return role === 'owner' || role === 'editor';
}

function canToggle(role: string): boolean {
  return role === 'owner' || role === 'editor' || role === 'checker';
}

// ── Task row ──────────────────────────────────────────────────────────────────

interface TaskRowProps {
  task: SharedListTask;
  myRole: string;
  onToggle: (taskId: string, completed: boolean) => void;
  onDelete: (taskId: string) => void;
  indent?: boolean;
}

function TaskRow({ task, myRole, onToggle, onDelete, indent = false }: TaskRowProps) {
  return (
    <li className={indent ? 'ml-6' : ''}>
      <div className="group flex items-center gap-3 py-2 px-1 rounded hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors">
        {/* Square checkbox consistent with mobile design */}
        <button
          onClick={() => canToggle(myRole) && onToggle(task.id, !task.isCompleted)}
          disabled={!canToggle(myRole)}
          className={[
            'w-4 h-4 shrink-0 border-2 rounded-sm flex items-center justify-center transition-colors',
            task.isCompleted
              ? 'bg-primary-600 border-primary-600'
              : 'border-border-subtle-light dark:border-border-subtle-dark',
            !canToggle(myRole) ? 'cursor-default' : 'cursor-pointer',
          ].join(' ')}
          aria-label={task.isCompleted ? 'Mark incomplete' : 'Mark complete'}
        >
          {task.isCompleted && <Check className="w-2.5 h-2.5 text-white" />}
        </button>

        <span
          className={[
            'flex-1 text-sm font-mono',
            task.isCompleted
              ? 'line-through text-text-tertiary-light dark:text-text-tertiary-dark'
              : 'text-text-primary-light dark:text-text-primary-dark',
          ].join(' ')}
        >
          {task.content}
        </span>

        {canWrite(myRole) && (
          <button
            onClick={() => onDelete(task.id)}
            className="opacity-0 group-hover:opacity-100 p-1 text-text-tertiary-light dark:text-text-tertiary-dark hover:text-red-500 dark:hover:text-red-400 rounded transition-all"
            aria-label="Delete task"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Subtasks */}
      {task.subtasks && task.subtasks.length > 0 && (
        <ul className="space-y-0">
          {task.subtasks.map((sub) => (
            <TaskRow
              key={sub.id}
              task={{ ...sub, subtasks: [] }}
              myRole={myRole}
              onToggle={onToggle}
              onDelete={onDelete}
              indent
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function SharedListDetailPage() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<SharedListDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Rename
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Add task
  const [newTaskContent, setNewTaskContent] = useState('');
  const [isAddingTask, setIsAddingTask] = useState(false);

  // Invite form
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('editor');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  // Global action errors
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!listId) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await getSharedList(listId);
      setDetail(data);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load list.');
    } finally {
      setIsLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Focus rename input when entering rename mode
  useEffect(() => {
    if (isRenaming) {
      requestAnimationFrame(() => renameInputRef.current?.select());
    }
  }, [isRenaming]);

  const handleRenameStart = () => {
    if (!detail) return;
    setRenameValue(detail.name);
    setIsRenaming(true);
  };

  const handleRenameSubmit = async () => {
    if (!listId || !detail || !renameValue.trim()) {
      setIsRenaming(false);
      return;
    }
    const name = renameValue.trim();
    if (name === detail.name) {
      setIsRenaming(false);
      return;
    }
    try {
      await renameSharedList(listId, name);
      setDetail((prev) => (prev ? { ...prev, name } : prev));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to rename.');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') void handleRenameSubmit();
    if (e.key === 'Escape') setIsRenaming(false);
  };

  const handleAddTask = async () => {
    const content = newTaskContent.trim();
    if (!content || !listId) return;
    setIsAddingTask(true);
    try {
      const task = await createSharedListTask(listId, { content });
      setDetail((prev) =>
        prev ? { ...prev, tasks: [...prev.tasks, task] } : prev
      );
      setNewTaskContent('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add task.');
    } finally {
      setIsAddingTask(false);
    }
  };

  const handleAddTaskKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') void handleAddTask();
  };

  const handleToggleTask = async (taskId: string, isCompleted: boolean) => {
    if (!listId || !detail) return;
    // Optimistic update
    setDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map((t) =>
          t.id === taskId
            ? { ...t, isCompleted }
            : {
                ...t,
                subtasks: t.subtasks.map((s) =>
                  s.id === taskId ? { ...s, isCompleted } : s
                ),
              }
        ),
      };
    });
    try {
      await updateSharedListTask(listId, taskId, { isCompleted });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update task.');
      // Revert
      void load();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!listId) return;
    // Optimistic remove
    setDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks
          .filter((t) => t.id !== taskId)
          .map((t) => ({ ...t, subtasks: t.subtasks.filter((s) => s.id !== taskId) })),
      };
    });
    try {
      await deleteSharedListTask(listId, taskId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete task.');
      void load();
    }
  };

  const handleReset = async () => {
    if (!listId || !window.confirm('Mark all tasks as incomplete?')) return;
    try {
      await resetSharedListTasks(listId);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reset tasks.');
    }
  };

  const handleDelete = async () => {
    if (!listId || !window.confirm('Delete this list? This cannot be undone.')) return;
    try {
      await deleteSharedList(listId);
      navigate('/app/shared-lists', { replace: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete list.');
    }
  };

  const handleLeave = async () => {
    if (!listId || !window.confirm('Leave this list?')) return;
    try {
      await leaveSharedList(listId);
      navigate('/app/shared-lists', { replace: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to leave list.');
    }
  };

  const handleInvite = async () => {
    if (!listId || !inviteEmail.trim()) return;
    setIsInviting(true);
    setInviteError(null);
    setInviteSuccess(null);
    try {
      await inviteToSharedList(listId, inviteEmail.trim(), inviteRole);
      setInviteSuccess(`Invite sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invite.');
    } finally {
      setIsInviting(false);
    }
  };

  const handleMemberRoleChange = async (member: SharedListMember, newRole: string) => {
    if (!listId) return;
    try {
      await changeSharedListMemberRole(listId, member.userId, newRole);
      setDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          members: prev.members.map((m) =>
            m.userId === member.userId ? { ...m, role: newRole } : m
          ),
        };
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to change role.');
    }
  };

  const handleMemberRemove = async (member: SharedListMember) => {
    if (!listId || !window.confirm(`Remove ${member.name} from this list?`)) return;
    try {
      await removeSharedListMember(listId, member.userId);
      setDetail((prev) => {
        if (!prev) return prev;
        return { ...prev, members: prev.members.filter((m) => m.userId !== member.userId) };
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove member.');
    }
  };

  // ── Render ──

  if (isLoading) {
    return (
      <Layout breadcrumb="Shared Lists">
        <div className="flex items-center justify-center h-full py-24">
          <Loader2 className="w-6 h-6 animate-spin text-text-tertiary-light dark:text-text-tertiary-dark" />
        </div>
      </Layout>
    );
  }

  if (loadError || !detail) {
    return (
      <Layout breadcrumb="Shared Lists">
        <div className="max-w-2xl mx-auto px-4 py-8 text-center space-y-3">
          <p className="text-sm text-red-600 dark:text-red-400">{loadError ?? 'List not found.'}</p>
          <Link
            to="/app/shared-lists"
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            Back to lists
          </Link>
        </div>
      </Layout>
    );
  }

  const { name, myRole, members, tasks } = detail;
  const isOwnerOrEditor = canWrite(myRole);
  const isOwner = myRole === 'owner';

  return (
    <Layout breadcrumb={name}>
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">

        {/* Back nav */}
        <Link
          to="/app/shared-lists"
          className="inline-flex items-center gap-1.5 text-xs text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark transition-colors font-mono"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Shared Lists
        </Link>

        {/* List header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {isRenaming ? (
              <input
                ref={renameInputRef}
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => void handleRenameSubmit()}
                onKeyDown={handleRenameKeyDown}
                className="w-full text-xl font-semibold font-mono bg-transparent border-b-2 border-primary-500 text-text-primary-light dark:text-text-primary-dark focus:outline-none pb-0.5"
              />
            ) : (
              <h1
                className={[
                  'text-xl font-semibold font-mono text-text-primary-light dark:text-text-primary-dark',
                  isOwnerOrEditor ? 'cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors' : '',
                ].join(' ')}
                title={isOwnerOrEditor ? 'Click to rename' : undefined}
                onClick={isOwnerOrEditor ? handleRenameStart : undefined}
              >
                {name}
              </h1>
            )}
            <p className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark mt-1 font-mono">
              {roleLabel(myRole)}
              {isOwnerOrEditor && ' · click name to rename'}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 shrink-0">
            {isOwnerOrEditor && (
              <button
                onClick={() => void handleReset()}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-mono text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded transition-colors"
                title="Mark all tasks incomplete"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset
              </button>
            )}
            {!isOwner && (
              <button
                onClick={() => void handleLeave()}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-mono text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                title="Leave this list"
              >
                <LogOut className="w-3.5 h-3.5" />
                Leave
              </button>
            )}
            {isOwner && (
              <button
                onClick={() => void handleDelete()}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-mono text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                title="Delete this list"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
            )}
          </div>
        </div>

        {/* Global error banner */}
        {actionError && (
          <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300 flex items-center justify-between gap-2">
            <span>{actionError}</span>
            <button
              onClick={() => setActionError(null)}
              className="text-red-500 hover:text-red-700 dark:hover:text-red-300 text-xs"
            >
              ✕
            </button>
          </div>
        )}

        {/* Task list */}
        <section className="space-y-2">
          <h2 className="text-xs font-mono uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary-dark">
            Tasks ({tasks.filter((t) => t.isCompleted).length}/{tasks.length})
          </h2>

          {tasks.length === 0 ? (
            <p className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark py-4 text-center">
              No tasks yet.
            </p>
          ) : (
            <ul className="space-y-0 divide-y divide-border-subtle-light dark:divide-border-subtle-dark">
              {tasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  myRole={myRole}
                  onToggle={(id, completed) => void handleToggleTask(id, completed)}
                  onDelete={(id) => void handleDeleteTask(id)}
                />
              ))}
            </ul>
          )}

          {/* Add task input */}
          {isOwnerOrEditor && (
            <div className="flex items-center gap-2 pt-1">
              <input
                type="text"
                value={newTaskContent}
                onChange={(e) => setNewTaskContent(e.target.value)}
                onKeyDown={handleAddTaskKeyDown}
                placeholder="Add a task…"
                className="flex-1 px-3 py-2 text-sm bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <button
                onClick={() => void handleAddTask()}
                disabled={isAddingTask || !newTaskContent.trim()}
                className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded transition-colors"
              >
                {isAddingTask ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
              </button>
            </div>
          )}
        </section>

        {/* Members section */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-mono uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary-dark">
              Members ({members.length})
            </h2>
            {isOwnerOrEditor && (
              <button
                onClick={() => {
                  setShowInviteForm(!showInviteForm);
                  setInviteEmail('');
                  setInviteError(null);
                  setInviteSuccess(null);
                }}
                className="flex items-center gap-1.5 text-xs font-mono text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
              >
                <UserPlus className="w-3.5 h-3.5" />
                Invite
              </button>
            )}
          </div>

          {/* Invite form */}
          {showInviteForm && (
            <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void handleInvite(); }}
                  placeholder="email@example.com"
                  className="flex-1 px-3 py-1.5 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <div className="relative">
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as Role)}
                    className="appearance-none pl-2.5 pr-6 py-1.5 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {roleLabel(r)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-text-tertiary-light dark:text-text-tertiary-dark" />
                </div>
              </div>

              {inviteError && (
                <p className="text-xs text-red-600 dark:text-red-400">{inviteError}</p>
              )}
              {inviteSuccess && (
                <p className="text-xs text-green-600 dark:text-green-400">{inviteSuccess}</p>
              )}

              <div className="flex items-center gap-2">
                <button
                  onClick={() => void handleInvite()}
                  disabled={isInviting || !inviteEmail.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium rounded transition-colors"
                >
                  {isInviting && <Loader2 className="w-3 h-3 animate-spin" />}
                  Send invite
                </button>
                <button
                  onClick={() => setShowInviteForm(false)}
                  className="px-3 py-1.5 text-xs text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Member list */}
          <ul className="space-y-1">
            {members.map((member) => (
              <li
                key={member.userId}
                className="flex items-center gap-3 px-3 py-2 rounded hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
              >
                {/* Avatar placeholder */}
                <div className="w-7 h-7 rounded-full bg-primary-700 dark:bg-primary-800 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {member.name.substring(0, 2).toUpperCase()}
                </div>
                <span className="flex-1 text-sm font-mono text-text-primary-light dark:text-text-primary-dark truncate">
                  {member.name}
                </span>

                {/* Role — owner can change it for non-owners */}
                {isOwner && member.role !== 'owner' ? (
                  <div className="relative">
                    <select
                      value={member.role}
                      onChange={(e) => void handleMemberRoleChange(member, e.target.value)}
                      className="appearance-none pl-2 pr-5 py-0.5 text-xs bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded text-text-secondary-light dark:text-text-secondary-dark focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {roleLabel(r)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="w-3 h-3 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none text-text-tertiary-light dark:text-text-tertiary-dark" />
                  </div>
                ) : (
                  <span className="text-xs font-mono text-text-tertiary-light dark:text-text-tertiary-dark">
                    {roleLabel(member.role)}
                  </span>
                )}

                {/* Remove member (owner only, not themselves) */}
                {isOwner && member.role !== 'owner' && (
                  <button
                    onClick={() => void handleMemberRemove(member)}
                    className="p-1 text-text-tertiary-light dark:text-text-tertiary-dark hover:text-red-500 dark:hover:text-red-400 rounded transition-colors"
                    aria-label={`Remove ${member.name}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Layout>
  );
}
